import { inject, Pipe, PipeTransform } from '@angular/core';
import { of } from 'rxjs';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { EntDescription } from '@bazis/shared/models/srv.types';

/**
 * Entity pipe
 *
 * A simple Angular pipe that resolves an entity by id and type using
 * `BazisEntityService` and returns an Observable. Use it together with
 * the `async` pipe in templates.
 *
 * Typical use:
 * - When you have an entity id and its type and need to get entity data
 * - When you already have an `EntDescription` object with `id` and `type` (typical relationship property)
 *
 * Examples:
 * ```html
 * <!-- Id + type (best practice: cache with @let, then guard with @if) -->
 * @let user = (userId | entity:'user.user' | async);
 * @if (user) {
 *   {{ user.$snapshot.name }}
 * }
 *
 * <!-- EntDescription object (typical relationship property) -->
 * @let user = ({ id: userId, type: 'user.user' } | entity | async);
 * @if (user) {
 *   {{ user.$snapshot.name }}
 * }
 *
 * <!-- List of ids -->
 * @for (id of userIds; track id) {
 *   @let user = (id | entity:'user.user' | async);
 *   @if (user) {
 *     <div>{{ user.$snapshot.name }}</div>
 *   }
 * }
 * ```
 */
@Pipe({
    name: 'entity',
    standalone: true,
})
export class EntityPipe implements PipeTransform {
    protected entityService = inject(BazisEntityService);

    /**
     * Resolve an entity as an Observable by id and type or by `EntDescription`.
     * Use with the `async` pipe.
     *
     * @param id - Entity id as string, or an `EntDescription` with `id` and `type`.
     * @param entityType - Entity type (required when `id` is a string). Ignored when `id` is an object.
     * @param forceLoad - If true, forces reload from API even if cached. Default: false.
     * @param doNotInitLoad - If true, do not start initial load (pair with `forceLoad` in custom flows). Default: false.
     * @returns Observable of the requested entity or `of(null)` when `id` is falsy.
     */
    transform(
        id: string | EntDescription,
        entityType: string = '',
        forceLoad = false,
        doNotInitLoad = false,
    ) {
        if (!id) return of(null);
        if (id instanceof Object)
            return this.entityService.getEntity$(id.type, id.id, { forceLoad, doNotInitLoad });

        return this.entityService.getEntity$(entityType, id, { forceLoad, doNotInitLoad });
    }
}
